import { useState } from "react";
import { useAdminData } from "@/hooks/useAdminData";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { LogIn, CalendarPlus, XCircle, RefreshCw, Search } from "lucide-react";

const statusColors: Record<string, string> = {
  trialing: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  past_due: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
};

const AdminUsers = () => {
  const { loading, error, organisations, profiles, authUsers, subscriptions, refetch } = useAdminData();
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Extend trial dialog
  const [trialDialog, setTrialDialog] = useState<{ orgId: string; orgName: string } | null>(null);
  const [trialDays, setTrialDays] = useState("14");

  // Status change dialog
  const [statusDialog, setStatusDialog] = useState<{ orgId: string; orgName: string } | null>(null);
  const [newStatus, setNewStatus] = useState("active");

  const filtered = organisations.filter((org) => {
    const q = search.toLowerCase();
    const orgProfiles = profiles.filter((p) => p.organisation_id === org.id);
    return (
      org.name.toLowerCase().includes(q) ||
      (org.email || "").toLowerCase().includes(q) ||
      orgProfiles.some((p) => (p.email || "").toLowerCase().includes(q) || (p.first_name || "").toLowerCase().includes(q))
    );
  });

  const handleImpersonate = async (userId: string) => {
    const profile = profiles.find((p) => p.user_id === userId);
    const authUser = authUsers.find((u) => u.id === userId);
    const email = authUser?.email || profile?.email;
    if (!email) {
      toast({ title: "Error", description: "No email found for this user", variant: "destructive" });
      return;
    }
    setActionLoading(userId);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("admin-impersonate", {
        body: { target_email: email },
      });
      if (fnError || data?.error) throw new Error(data?.error || fnError?.message);
      window.open(data.url, "_blank");
      toast({ title: "Impersonation link opened", description: "Check the new tab. Use incognito if you want to keep your session." });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleExtendTrial = async () => {
    if (!trialDialog) return;
    setActionLoading(trialDialog.orgId);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("admin-manage-user", {
        body: { action: "extend_trial", organisation_id: trialDialog.orgId, days: parseInt(trialDays) },
      });
      if (fnError || data?.error) throw new Error(data?.error || fnError?.message);
      toast({ title: "Trial extended", description: `Trial extended by ${trialDays} days` });
      setTrialDialog(null);
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelSubscription = async (orgId: string) => {
    setActionLoading(orgId);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("admin-manage-user", {
        body: { action: "cancel_subscription", organisation_id: orgId },
      });
      if (fnError || data?.error) throw new Error(data?.error || fnError?.message);
      toast({ title: "Subscription cancelled" });
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangeStatus = async () => {
    if (!statusDialog) return;
    setActionLoading(statusDialog.orgId);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("admin-manage-user", {
        body: { action: "update_subscription_status", organisation_id: statusDialog.orgId, status: newStatus },
      });
      if (fnError || data?.error) throw new Error(data?.error || fnError?.message);
      toast({ title: "Status updated", description: `Status changed to ${newStatus}` });
      setStatusDialog(null);
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Users & Organisations</h1>
        <Button variant="outline" size="sm" onClick={refetch}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="space-y-3">
        {filtered.map((org) => {
          const orgProfiles = profiles.filter((p) => p.organisation_id === org.id);
          const sub = subscriptions.find((s) => s.organisation_id === org.id);

          return (
            <Card key={org.id}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{org.name}</CardTitle>
                    <p className="text-xs text-muted-foreground">{org.email || "No email"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={statusColors[org.subscription_status] || ""}>{org.subscription_status}</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">Trial ends:</span>{" "}
                    <span className="font-medium">{format(new Date(org.trial_ends_at), "dd MMM yyyy")}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Created:</span>{" "}
                    <span className="font-medium">{format(new Date(org.created_at), "dd MMM yyyy")}</span>
                  </div>
                  {sub && (
                    <>
                      <div>
                        <span className="text-muted-foreground">Sub status:</span>{" "}
                        <span className="font-medium">{sub.status}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Next payment:</span>{" "}
                        <span className="font-medium">{sub.next_payment_date ? format(new Date(sub.next_payment_date), "dd MMM yyyy") : "—"}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Users in org */}
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Users ({orgProfiles.length})</p>
                  {orgProfiles.map((p) => {
                    const au = authUsers.find((u) => u.id === p.user_id);
                    return (
                      <div key={p.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                        <div>
                          <span className="font-medium">{[p.first_name, p.last_name].filter(Boolean).join(" ") || "—"}</span>
                          <span className="text-muted-foreground ml-2">{p.email || au?.email}</span>
                          {au?.last_sign_in_at && (
                            <span className="text-muted-foreground ml-2 text-xs">
                              Last login: {format(new Date(au.last_sign_in_at), "dd MMM yyyy HH:mm")}
                            </span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={actionLoading === p.user_id}
                          onClick={() => handleImpersonate(p.user_id)}
                          title="Log in as this user"
                        >
                          <LogIn className="h-4 w-4 mr-1" /> Impersonate
                        </Button>
                      </div>
                    );
                  })}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button variant="outline" size="sm" onClick={() => { setTrialDialog({ orgId: org.id, orgName: org.name }); setTrialDays("14"); }}>
                    <CalendarPlus className="h-4 w-4 mr-1" /> Extend Trial
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setStatusDialog({ orgId: org.id, orgName: org.name }); setNewStatus("active"); }}>
                    <RefreshCw className="h-4 w-4 mr-1" /> Change Status
                  </Button>
                  {org.subscription_status !== "cancelled" && (
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={actionLoading === org.id}
                      onClick={() => handleCancelSubscription(org.id)}
                    >
                      <XCircle className="h-4 w-4 mr-1" /> Cancel Subscription
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-center text-muted-foreground py-8">No organisations found</p>
        )}
      </div>

      {/* Extend Trial Dialog */}
      <Dialog open={!!trialDialog} onOpenChange={(o) => !o && setTrialDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Extend Trial — {trialDialog?.orgName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Days to add</label>
            <Input type="number" min="1" max="365" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrialDialog(null)}>Cancel</Button>
            <Button onClick={handleExtendTrial} disabled={!!actionLoading}>Extend</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Status Dialog */}
      <Dialog open={!!statusDialog} onOpenChange={(o) => !o && setStatusDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Status — {statusDialog?.orgName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">New status</label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="trialing">Trialing</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="past_due">Past Due</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(null)}>Cancel</Button>
            <Button onClick={handleChangeStatus} disabled={!!actionLoading}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
